import adaEmbed from '@ada-support/embed2';
import { Body4, Body5, H5 } from '@cointracker/styleguide/src/LoggedIn';
import { REBRAND_URLS } from '@cointracker/ui';
import {
  ArrowLeft,
  Files,
  IdentificationBadge,
  SealCheck,
  Stethoscope,
} from '@phosphor-icons/react';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from 'src/app/analytics';
import { UserContext } from 'src/app/user/context';
import { URLS } from 'src/common/urls';
import { useTaxReadiness } from '../../../hooks/useTaxReadiness';
import { isAuthenticatedUser } from '../../../utils';

interface HelpScreenProps {
  onBack: () => void;
}

export const HelpScreen = ({ onBack }: HelpScreenProps) => {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const {
    addWalletsStepChecked,
    syncWalletsStepChecked,
    optimizeTaxSavingsStepChecked,
    reviewTaxFormsStepChecked,
  } = useTaxReadiness();
  const analytics = useAnalytics();

  const onClickChatWithSupport = useCallback(async () => {
    if (!isAuthenticatedUser(user)) return;
    analytics.track('Tax Readiness Clicked', {
      action: 'Chat With Support',
      step_1_completed: addWalletsStepChecked,
      step_2_completed: syncWalletsStepChecked,
      step_3_completed: optimizeTaxSavingsStepChecked,
      step_4_completed: reviewTaxFormsStepChecked,
    });
    try {
      if ((await adaEmbed.getInfo()).hasActiveChatter) return adaEmbed.stop();
    } catch {
      // can't get info on adaEmbed if it isn't started
    }
    const subscriptionPlan = user.unifiedSubscription?.subscriptionPlan ?? '';
    try {
      await adaEmbed?.start({
        handle: 'cointracker',
        metaFields: {
          meta_user_authorized: user.isAuthenticated,
          meta_ct_user_2FA_enabled: user.isTwoStepVerificationEnabled,
          meta_ct_user_email: user.email,
          meta_ct_portfolio_subscription: user.portfolioSubscription,
          meta_ct_unified_plan:
            subscriptionPlan.charAt(0).toUpperCase() +
            subscriptionPlan.substring(1).toLowerCase(),
        },
      });
      await adaEmbed.toggle();
    } catch {
      // Supressing errors like "ada embed already started etc" they are not actionable either way
    }
  }, [
    user,
    addWalletsStepChecked,
    syncWalletsStepChecked,
    optimizeTaxSavingsStepChecked,
    reviewTaxFormsStepChecked,
    analytics,
  ]);

  const trackAndOpenLink = (url: string, action: string) => {
    analytics.track('Tax Readiness Clicked', {
      action,
      step_1_completed: addWalletsStepChecked,
      step_2_completed: syncWalletsStepChecked,
      step_3_completed: optimizeTaxSavingsStepChecked,
      step_4_completed: reviewTaxFormsStepChecked,
    });
    window.open(url, '_blank');
  };

  return (
    <div className="flex flex-col gap-48">
      <div className="flex flex-col gap-8">
        <button
          onClick={onBack}
          className="flex items-center gap-8 text-text-primary"
        >
          <ArrowLeft size={20} weight="bold" /> {t('button.back')}
        </button>

        <H5 className="mb-8 mt-8 text-text-primary-foreground">
          {t('navigation.tax_readiness.sheet.help_screen.title')}
        </H5>

        <Body4 className="text-text-secondary">
          {t('navigation.tax_readiness.sheet.help_screen.description')}
        </Body4>
      </div>

      <div className="grid grid-cols-2 gap-16">
        <button
          className="flex flex-col items-center rounded-16 border border-line-card pb-32 pl-24 pr-24 pt-32 hover:bg-line-secondary focus:border-line-card-highlight focus:bg-background-card-highlight"
          onClick={() =>
            trackAndOpenLink(REBRAND_URLS.BLOG_TAX_GUIDE, 'Read Guide')
          }
        >
          <div className="mb-12 flex h-48 w-48 items-center justify-center rounded-8 bg-background-alternate-consistent">
            <Files
              size={24}
              weight="regular"
              className="text-text-primary-foreground-inverse-consistent"
            />
          </div>
          <Body4 className="text-text-primary-foreground">
            {t('navigation.tax_readiness.sheet.help_screen.finish_taxes')}
          </Body4>
          <Body5 className="text-text-secondary">
            {t('navigation.tax_readiness.sheet.help_screen.read_guide')}
          </Body5>
        </button>

        <button
          className="flex flex-col items-center rounded-16 border border-line-card pb-32 pl-24 pr-24 pt-32 hover:bg-line-secondary focus:border-line-card-highlight focus:bg-background-card-highlight"
          onClick={onClickChatWithSupport}
        >
          <div className="mb-12 flex h-48 w-48 items-center justify-center rounded-8 bg-background-alternate-consistent">
            <IdentificationBadge
              size={24}
              weight="regular"
              className="text-text-primary-foreground-inverse-consistent"
            />
          </div>
          <Body4 className="text-text-primary-foreground">
            {t('navigation.tax_readiness.sheet.help_screen.contact_support')}
          </Body4>
          <Body5 className="text-text-secondary">
            {t('navigation.tax_readiness.sheet.help_screen.get_help')}
          </Body5>
        </button>

        <button
          className="flex flex-col items-center rounded-16 border border-line-card pb-32 pl-24 pr-24 pt-32 hover:bg-line-secondary focus:border-line-card-highlight focus:bg-background-card-highlight"
          onClick={() =>
            trackAndOpenLink(
              'https://webforms.pipedrive.com/f/clNHcKjtzUSG7y3o8wT3ESOfeHpaFtfCzVL55DXlT78FImcNh0tGmb67bJfldlqKJR',
              'Get Reconciliation Support',
            )
          }
        >
          <div className="mb-12 flex h-48 w-48 items-center justify-center rounded-8 bg-background-alternate-consistent">
            <Stethoscope
              size={24}
              weight="regular"
              className="text-text-primary-foreground-inverse-consistent"
            />
          </div>
          <Body4 className="text-text-primary-foreground">
            {t(
              'navigation.tax_readiness.sheet.help_screen.get_reconciliation_support',
            )}
          </Body4>
          <Body5 className="text-text-secondary">
            {t('navigation.tax_readiness.sheet.help_screen.hire_an_expert')}
          </Body5>
        </button>

        <button
          className="flex flex-col items-center rounded-16 border border-line-card pb-32 pl-24 pr-24 pt-32 hover:bg-line-secondary focus:border-line-card-highlight focus:bg-background-card-highlight"
          onClick={() =>
            trackAndOpenLink(URLS.FULL_SERVICE_PAGE, 'Go Full Service')
          }
        >
          <div className="mb-12 flex h-48 w-48 items-center justify-center rounded-8 bg-background-alternate-consistent">
            <SealCheck
              size={24}
              weight="regular"
              className="text-text-primary-foreground-inverse-consistent"
            />
          </div>
          <Body4 className="text-text-primary-foreground">
            {t('navigation.tax_readiness.sheet.help_screen.go_full_service')}
          </Body4>
          <Body5 className="text-text-secondary">
            {t('button.learn_more')}
          </Body5>
        </button>
      </div>
    </div>
  );
};
